import { formatUsd, JOB_PACK_DAYS, PRICES } from '~/constants/pricing'
import { LEGAL_OPERATOR, operatorLine, operatorVatLine, type LegalDocument } from './operator'

/** Terms of Use. Prices come from constants/pricing.ts so the text never drifts from checkout. */
export function termsOfUse(): LegalDocument {
  const o = LEGAL_OPERATOR
  const mail = `[${o.email}](mailto:${o.email})`

  return {
    intro: [
      `These Terms of Use govern your use of ${o.brand}, the online CV builder available at ${o.website.replace('https://', '')}, including the web CV, business card, cover letter and AI tools (together, the “Service”). By using the Service or creating an account you agree to these terms. If you do not agree, please do not use the Service.`,
    ],
    sections: [
      {
        id: 'operator',
        title: '1. Who we are',
        blocks: [
          `The Service is operated by: ${operatorLine()}`,
          'In these terms “we” and “us” mean the operator, and “you” means the person using the Service.',
        ],
      },
      {
        id: 'eligibility',
        title: '2. Eligibility and your account',
        blocks: [
          {
            list: [
              'You must be at least 16 years old to use the Service.',
              'Some features work without an account. To save CVs, publish a web CV or buy a paid plan you need an account, which you can create with an e-mail address or with Google.',
              'Give us accurate information and keep your login details secure. You are responsible for everything done through your account.',
              `Tell us at ${mail} straight away if you suspect someone else has access to your account.`,
            ],
          },
        ],
      },
      {
        id: 'service',
        title: '3. The Service and free use',
        blocks: [
          'The Service lets you create, edit, store and export CVs and cover letters, publish a web version of your CV, and use AI tools that draft, improve or analyse your text.',
          '**Free use.** You can create a CV and download it as a PDF for free. Free PDFs carry an InMyResume watermark. AI imports, PDF exports and some other features are limited per day for free users and visitors without an account; the current limits are shown in the app.',
          '**Premium features.** Watermark-free PDFs, higher or unlimited usage and other Premium features described on the [pricing page](/pricing) are available with a paid plan. "Unlimited" means as much as one person needs for their own job search: to keep the Service fast and affordable, AI features have a generous daily fair-use limit that stops automated or shared use.',
          'We keep improving the Service, so features, templates and limits may change. We will not remove a feature you have already paid for during the period you paid for without offering a fair alternative or a proportionate refund.',
        ],
      },
      {
        id: 'payments',
        title: '4. Paid plans and payment',
        blocks: [
          '**Purchases in US dollars: Lemon Squeezy is the seller.** They are processed by Lemon Squeezy LLC, which acts as merchant of record. It handles the checkout, payment, invoice and sales tax or VAT, and your purchase is also subject to the Lemon Squeezy buyer terms shown at checkout. Taxes may be added at checkout depending on where you live.',
          `**Purchases in Czech crowns or Polish złoty: we are the seller.** When you use the site in Czech, prices are shown and charged in Czech crowns; in Polish, in Polish złoty, by card, BLIK or Przelewy24. We sell those purchases ourselves and issue the receipt or invoice; the payment is processed by Stripe Payments Europe, Ltd. ${operatorVatLine()} Crown and złoty prices are therefore final.`,
          'The plans currently offered are:',
          {
            list: [
              `**Single PDF export (${formatUsd(PRICES.exportCredit)}).** A one-time payment for one watermark-free PDF download. The credit does not expire.`,
              `**Job Search Pack (${formatUsd(PRICES.jobPack)}).** A one-time payment for ${JOB_PACK_DAYS} days of Premium, starting when the payment is confirmed. It does not renew automatically. A web CV published with the pack stays online while the pack runs and goes offline when it ends; another pack lets you publish it again.`,
              `**Yearly Premium (${formatUsd(PRICES.yearly)} per year).** A subscription that **renews automatically** every year at the price in effect at that time until you cancel it.`,
            ],
          },
          'You can cancel a subscription at any time from the link in your Lemon Squeezy receipt e-mail (a subscription paid in Czech crowns or Polish złoty: “Manage subscription” on the Premium page) or by writing to us. Cancellation stops the next renewal; Premium stays active until the end of the period already paid for.',
          'Existing subscriptions to plans we no longer sell (such as the former monthly plan) continue under the terms and price agreed when you bought them until you cancel.',
          'If we change the price of a subscription, we will tell you at least 30 days before the change applies to your next renewal, so you can cancel beforehand.',
        ],
      },
      {
        id: 'refunds',
        title: '5. Right of withdrawal and refunds',
        blocks: [
          'If you are a consumer in the EU, you normally have 14 days to withdraw from an online purchase. For digital content and services that start immediately, such as a watermark-free export or Premium access, you ask us at checkout to start straight away and acknowledge that you **lose the right of withdrawal** once delivery has begun. This does not affect your rights if the Service is faulty.',
          `Beyond the law, if something went wrong — for example you were charged twice, a renewal happened by mistake and you have not used Premium since, or a paid feature did not work — write to ${mail} within 14 days of the charge and we will look at it fairly and refund you where it is justified. Refunds are paid to the original payment method, by Lemon Squeezy or, for a purchase in Czech crowns or Polish złoty, by us through Stripe.`,
        ],
      },
      {
        id: 'your-content',
        title: '6. Your content',
        blocks: [
          'You keep all rights to the content you put into the Service: your texts, photos and CV data (“Your Content”).',
          'You give us a non-exclusive, worldwide, royalty-free licence to store, process, copy and display Your Content only as needed to run the Service for you — for example to save your CV, render it into a PDF, send it to an AI provider when you use an AI feature, or show your web CV to people you share it with. The licence ends when you delete the content or your account, except for copies in backups kept for a limited time.',
          'You are responsible for Your Content. Make sure it is truthful, that you have the right to use it (including any photo), and that personal data of other people, such as referees, is included with their permission.',
          '**Public web CV.** If you publish your web CV or business card, anyone with the link can view it and search engines may index it. You can unpublish it at any time. Publishing is part of Premium, so a web CV goes offline when your Premium access ends.',
          'How we handle personal data is described in our [Privacy Policy](/privacy).',
        ],
      },
      {
        id: 'ai',
        title: '7. AI features',
        blocks: [
          'AI features generate suggestions automatically using third-party models. Results can be inaccurate, incomplete or inappropriate for your situation. **Always review AI output before you use it** and do not add experience, qualifications or achievements you do not have.',
          'The ATS check and job match scores are estimates to help you improve your CV. They do not predict how any employer or applicant tracking system will rate your application, and we do not guarantee interviews, job offers or any other outcome.',
        ],
      },
      {
        id: 'acceptable-use',
        title: '8. Acceptable use',
        blocks: [
          'You must not:',
          {
            list: [
              'use the Service to create false or misleading documents intended to deceive employers or others, or to impersonate another person;',
              'upload content that is illegal, defamatory, discriminatory, sexually explicit or that infringes someone else’s rights;',
              'publish other people’s personal data without a legal basis;',
              'try to get around usage limits, watermarks or payments, for example by creating multiple accounts or removing watermarks;',
              'scrape, copy or resell the Service, its templates or its AI output at scale, or use it to build a competing product;',
              'probe, overload or attack the Service, or access accounts or data that are not yours;',
              'use the Service in a way that breaks the law or the rules of our providers, including OpenAI’s usage policies.',
            ],
          },
        ],
      },
      {
        id: 'our-rights',
        title: '9. Our intellectual property',
        blocks: [
          'The Service, its design, code, CV templates, texts, logos and the InMyResume name belong to us or our licensors. You may use the templates to create CVs and cover letters for yourself and share or print the resulting documents freely. You may not copy the templates or other parts of the Service for any other purpose.',
        ],
      },
      {
        id: 'availability',
        title: '10. Availability',
        blocks: [
          'We work to keep the Service available and your data safe, but we cannot guarantee uninterrupted or error-free operation. Maintenance, provider outages or events beyond our control may make it temporarily unavailable. Please keep your own copy of important documents, for example by downloading the PDF.',
        ],
      },
      {
        id: 'liability',
        title: '11. Liability',
        blocks: [
          'Nothing in these terms limits liability that cannot be limited by law, including liability for intent or gross negligence, or your statutory rights as a consumer.',
          'Otherwise, we are not liable for indirect or consequential loss, such as lost earnings or missed job opportunities, and our total liability to you for any claim related to the Service is limited to the amount you paid us in the 12 months before the claim arose, or USD 50 if you have not paid anything.',
        ],
      },
      {
        id: 'termination',
        title: '12. Ending your use',
        blocks: [
          `You can stop using the Service at any time. To delete your account and your content, write to ${mail} from the e-mail address linked to your account. Deleting an account does not cancel an active subscription automatically; please cancel it first.`,
          'We may suspend or close an account that seriously or repeatedly breaks these terms, or where the law requires it. Unless the breach is serious or urgent, we will warn you first and give you a chance to download your content. If we close your account without a breach on your part, we will refund the unused part of any prepaid period.',
        ],
      },
      {
        id: 'changes',
        title: '13. Changes to these terms',
        blocks: [
          'We may update these terms when the Service or the law changes. The date at the top shows the latest version. We will tell you about material changes by e-mail or in the app at least 14 days before they take effect. If you do not agree, you can stop using the Service and cancel your subscription before the change applies.',
        ],
      },
      {
        id: 'law',
        title: '14. Governing law and disputes',
        blocks: [
          `These terms are governed by the laws of the ${o.country}. If you are a consumer, you also keep the protection of the mandatory laws of the country where you live, and you can bring a claim in the courts there.`,
          `Please contact us first at ${mail}; most issues can be solved quickly. Consumers in the Czech Republic can also turn to the Czech Trade Inspection Authority ([coi.cz](https://www.coi.cz)) for out-of-court resolution of consumer disputes.`,
        ],
      },
      {
        id: 'contact',
        title: '15. Contact',
        blocks: [
          `Questions about these terms? Write to ${mail} or use our [contact form](/contact).`,
        ],
      },
    ],
  }
}
